/**
 * Equipment Context
 * 
 * Context provider for sharing equipment state across components.
 * Manages state for equipment selection, filtering, and data.
 */

import React, { createContext, useReducer, useContext, ReactNode, act } from 'react';
import {
  EquipmentHierarchy,
  EquipmentNode,
  TechnologyType,
  WorkOrderEvent,
  TrendChartData,
  KPIData,
  DateRange
} from '../services/equipment/types';
import { getDefaultDateRange } from '../components/equipment-status/date-range-selector/date-range-utils';

// Define the state interface
export interface EquipmentState {
  // Site and APM ID
  site: string | null;
  apmId: string | null;
  position: string | null;

  // Equipment hierarchy and selection
  equipmentHierarchy: EquipmentHierarchy | null;
  selectedNodeId: string | null;
  selectedNode: EquipmentNode | null;

  // Filtering
  dateRange: DateRange;

  // Data
  events: WorkOrderEvent[];
  trendChartData: Record<TechnologyType, TrendChartData | null>;
  kpiData: KPIData | null;

  // Site change state
  siteChangeInProgress: boolean;
  siteChangeError: string | null;
  siteChangeSuccess: boolean;

  // UI state
  isLoading: {
    hierarchy: boolean;
    events: boolean;
    trendCharts: boolean;
    kpi: boolean;
  };
  errors: {
    hierarchy: string | null;
    events: string | null;
    trendCharts: string | null;
    kpi: string | null;
  };
}

// Define action types
export type EquipmentActionType =
  | { type: 'SET_SITE_APM_ID'; payload: { site: string; apmId: string } }
  | { type: 'SET_POSITION'; payload: string | null }
  | { type: 'SET_EQUIPMENT_HIERARCHY'; payload: EquipmentHierarchy }
  | { type: 'SET_SELECTED_NODE_ID'; payload: string | null }
  | { type: 'SET_SELECTED_NODE'; payload: EquipmentNode | null }
  | { type: 'SET_DATE_RANGE'; payload: DateRange }
  | { type: 'SET_EVENTS'; payload: WorkOrderEvent[] }
  | { type: 'SET_TREND_CHART_DATA'; payload: { technology: TechnologyType; data: TrendChartData | null } }
  | { type: 'SET_KPI_DATA'; payload: KPIData }
  | { type: 'SET_LOADING'; payload: { key: keyof EquipmentState['isLoading']; value: boolean } }
  | { type: 'SET_ERROR'; payload: { key: keyof EquipmentState['errors']; value: string | null } }
  | { type: 'CHANGE_SITE'; payload: { site: string } }
  | { type: 'SET_SITE_CHANGE_LOADING'; payload: boolean }
  | { type: 'SET_SITE_CHANGE_ERROR'; payload: string | null }
  | { type: 'SET_SITE_CHANGE_SUCCESS'; payload: boolean }
  | { type: 'RESET_STATE' };

// Define the context interface
interface EquipmentContextType {
  state: EquipmentState;
  dispatch: React.Dispatch<EquipmentActionType>;
}

// Initial state
const initialState: EquipmentState = {
  site: null,
  apmId: null,
  position: null,
  equipmentHierarchy: null,
  selectedNodeId: null,
  selectedNode: null,
  dateRange: getDefaultDateRange(),
  events: [],
  trendChartData: {
    monitron: null,
    pulse: null,
    cadet: null,
    thermal: null
  },
  kpiData: null,
  siteChangeInProgress: false,
  siteChangeError: null,
  siteChangeSuccess: false,
  isLoading: {
    hierarchy: false,
    events: false,
    trendCharts: false,
    kpi: false
  },
  errors: {
    hierarchy: null,
    events: null,
    trendCharts: null,
    kpi: null
  }
};

// Create the context with undefined as default value to detect when used outside provider
export const EquipmentContext = createContext<EquipmentContextType | undefined>(undefined);

// Reducer function
const equipmentReducer = (state: EquipmentState, action: EquipmentActionType): EquipmentState => {
  switch (action.type) {
    case 'SET_SITE_APM_ID':
      return {
        ...state,
        site: action.payload.site,
        apmId: action.payload.apmId
      };

    case 'SET_POSITION':
      return {
        ...state,
        position: action.payload
      };

    case 'SET_EQUIPMENT_HIERARCHY':
      return {
        ...state,
        equipmentHierarchy: action.payload
      };

    case 'SET_SELECTED_NODE_ID':
      return {
        ...state,
        selectedNodeId: action.payload
      };

    case 'SET_SELECTED_NODE':
      if (action.payload?.type === 'position') {
        const tokens = action.payload.id.split('#');
        const apmId = tokens[0];
        const position = tokens[1];
        return {
          ...state,
          apmId,
          position,
          selectedNodeId: action.payload.id,
          selectedNode: action.payload,
        }
      }
      if (action.payload?.type === 'asset') {
        return {
          ...state,
          apmId: action.payload.id,
          position: null,
          selectedNodeId: action.payload.id,
          selectedNode: action.payload,
        }
      }
      return {...state};

    case 'SET_DATE_RANGE':
      return {
        ...state,
        dateRange: action.payload
      };

    case 'SET_EVENTS':
      return {
        ...state,
        events: action.payload
      };

    case 'SET_TREND_CHART_DATA':
      return {
        ...state,
        trendChartData: {
          ...state.trendChartData,
          [action.payload.technology]: action.payload.data
        }
      };

    case 'SET_KPI_DATA':
      return {
        ...state,
        kpiData: action.payload
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          [action.payload.key]: action.payload.value
        }
      };

    case 'SET_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.key]: action.payload.value
        }
      };

    case 'CHANGE_SITE':
      // Complete state cleanup when changing sites, clearing apmId
      return {
        ...initialState,
        site: action.payload.site,
        apmId: null, // Clear apmId when changing sites
        siteChangeInProgress: false,
        siteChangeError: null,
        siteChangeSuccess: false,
        // Preserve user preferences
        dateRange: state.dateRange
      };

    case 'SET_SITE_CHANGE_LOADING':
      return {
        ...state,
        siteChangeInProgress: action.payload,
        // Clear any previous site change errors and success when starting a new change
        siteChangeError: action.payload ? null : state.siteChangeError,
        siteChangeSuccess: action.payload ? false : state.siteChangeSuccess
      };

    case 'SET_SITE_CHANGE_ERROR':
      return {
        ...state,
        siteChangeError: action.payload,
        siteChangeInProgress: false,
        siteChangeSuccess: false
      };

    case 'SET_SITE_CHANGE_SUCCESS':
      return {
        ...state,
        siteChangeSuccess: action.payload,
        siteChangeError: action.payload ? null : state.siteChangeError
      };

    case 'RESET_STATE':
      return {
        ...initialState,
        // Preserve site and apmId when resetting
        site: state.site,
        apmId: state.apmId
      };

    default:
      return state;
  }
};

// Provider component
interface EquipmentProviderProps {
  children: ReactNode;
}

export const EquipmentProvider: React.FC<EquipmentProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(equipmentReducer, initialState);

  return (
    <EquipmentContext.Provider value={{ state, dispatch }}>
      {children}
    </EquipmentContext.Provider>
  );
};

// Custom hook for using the equipment context
export const useEquipmentContext = () => {
  const context = useContext(EquipmentContext);

  if (context === undefined) {
    throw new Error('useEquipmentContext must be used within an EquipmentProvider');
  }

  return context;
};